// ============================================================
//  Page /parcours/:id — editeur de parcours
//  Carte du lieu avec balises cliquables + panneau pour assigner
//  des questions a chaque balise.
// ============================================================

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import L from "leaflet";
import { useRef } from "react";
import * as api from "../lib/api";
import type {
  Balise,
  Lieu,
  Parcours,
  ParcoursEtape,
  Question,
} from "../lib/types";
import { NIVEAUX } from "../lib/types";

export function ParcoursEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [parcours, setParcours] = useState<Parcours | null>(null);
  const [etapes, setEtapes] = useState<ParcoursEtape[]>([]);
  const [lieu, setLieu] = useState<Lieu | null>(null);
  const [balises, setBalises] = useState<Balise[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { parcours: p, etapes: es } = await api.getParcoursWithEtapes(id);
        setParcours(p);
        const [l, bs] = await Promise.all([
          api.getLieu(p.lieu_id),
          api.listBalises(p.lieu_id),
        ]);
        setLieu(l);
        setBalises(bs);
        // Auto-affectation : pour chaque balise sans etape OU avec etape vide,
        // on tire une question aleatoire (filtrage par niveau du parcours si defini).
        const etapesAfterSeed = await seedRandomQuestions(p, bs, es);
        setEtapes(etapesAfterSeed);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  async function seedRandomQuestions(
    parc: Parcours,
    bs: Balise[],
    existing: ParcoursEtape[]
  ): Promise<ParcoursEtape[]> {
    const PER_BALISE = 10;
    const existingByBalise = new Map(existing.map((e) => [e.balise_id, e]));
    // On NE seed PAS les balises absentes du parcours (l'inclusion est explicite).
    // On top-up seulement les etapes deja presentes avec strictement moins de 10 q.
    const toTopUp = bs.filter((b) => {
      const e = existingByBalise.get(b.id);
      return e && e.question_ids.length < PER_BALISE;
    });
    if (toTopUp.length === 0) return existing;

    // Charge un pool de questions selon le niveau du parcours
    const pool = await api.listQuestions({
      niveau: parc.niveau ?? undefined,
      limit: 200,
    });
    let candidates = pool.questions;
    // Fallback : si rien au bon niveau, on prend toutes niveaux confondus
    if (candidates.length === 0) {
      const all = await api.listQuestions({ limit: 200 });
      candidates = all.questions;
    }
    if (candidates.length === 0) return existing; // pas de banque, silence

    const updated: ParcoursEtape[] = [...existing.filter((e) => !toTopUp.some((b) => b.id === e.balise_id))];
    for (const balise of toTopUp) {
      const current = existingByBalise.get(balise.id)?.question_ids ?? [];
      const alreadyIn = new Set(current);
      const missing = PER_BALISE - current.length;
      // Pioche missing questions distinctes, en evitant celles deja presentes
      const extras = shuffleCopy(candidates)
        .filter((q) => !alreadyIn.has(q.id))
        .slice(0, missing)
        .map((q) => q.id);
      const finalIds = [...current, ...extras];
      try {
        const etape = await api.upsertEtape(parc.id, balise.id, finalIds);
        updated.push(etape);
      } catch { /* on continue si une balise echoue */ }
    }
    return updated;
  }

  const etapeByBalise = useMemo(() => {
    const m = new Map<string, ParcoursEtape>();
    for (const e of etapes) m.set(e.balise_id, e);
    return m;
  }, [etapes]);

  const selectedBalise = balises.find((b) => b.id === selectedId) ?? null;
  const selectedEtape = selectedBalise ? etapeByBalise.get(selectedBalise.id) ?? null : null;

  async function setQuestionsForBalise(baliseId: string, questionIds: string[]) {
    if (!parcours) return;
    const etape = await api.upsertEtape(parcours.id, baliseId, questionIds);
    setEtapes((prev) => {
      const filtered = prev.filter((e) => e.balise_id !== baliseId);
      return [...filtered, etape];
    });
  }

  async function clearEtape(baliseId: string) {
    if (!parcours) return;
    await api.deleteEtape(parcours.id, baliseId);
    setEtapes((prev) => prev.filter((e) => e.balise_id !== baliseId));
  }

  /** Inclut une balise dans le parcours avec 10 questions aleatoires. */
  async function addBaliseToParcours(baliseId: string) {
    if (!parcours) return;
    const pool = await api.listQuestions({
      niveau: parcours.niveau ?? undefined,
      limit: 200,
    });
    let candidates = pool.questions;
    if (candidates.length === 0) {
      const all = await api.listQuestions({ limit: 200 });
      candidates = all.questions;
    }
    if (candidates.length === 0) {
      alert("Aucune question dans la banque. Ajoute des questions d'abord.");
      return;
    }
    const ids = shuffleCopy(candidates).slice(0, 10).map((q) => q.id);
    const etape = await api.upsertEtape(parcours.id, baliseId, ids);
    setEtapes((prev) => [...prev.filter((e) => e.balise_id !== baliseId), etape]);
  }

  /** Vide TOUT le parcours (utile apres ancien comportement qui seedait tout). */
  async function clearAllEtapes() {
    if (!parcours) return;
    if (!confirm(`Retirer les ${etapes.length} balise(s) de ce parcours ? (les questions de la banque restent intactes)`)) return;
    for (const e of etapes) {
      try { await api.deleteEtape(parcours.id, e.balise_id); } catch { /* continue */ }
    }
    setEtapes([]);
  }

  if (loading) return <div className="page-loading">Chargement…</div>;
  if (error) return <div className="page-error">Erreur : {error}</div>;
  if (!parcours || !lieu) return <div className="page-error">Parcours introuvable</div>;

  return (
    <div className="carte-page">
      <header className="carte-head">
        <div>
          <button onClick={() => navigate("/parcours")} className="btn-back">← Retour</button>
          <h1>{parcours.nom}</h1>
          <p className="carte-sub">
            {parcours.niveau && <span className="badge badge-niveau">{parcours.niveau}</span>}{" "}
            · {etapes.length} balise{etapes.length > 1 ? "s" : ""} dans le parcours / {balises.length} dispo
          </p>
        </div>
        {etapes.length > 0 && (
          <button onClick={clearAllEtapes} className="btn-danger" title="Retire toutes les balises du parcours">
            🗑 Vider le parcours
          </button>
        )}
      </header>

      <div className="carte-body">
        {hasAnyGps(balises) ? (
          <ParcoursSatelliteView
            balises={balises}
            etapeByBalise={etapeByBalise}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        ) : (
          <ParcoursMapView
            lieu={lieu}
            balises={balises}
            etapeByBalise={etapeByBalise}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        )}

        <aside className="balises-panel">
          {!selectedBalise ? (
            <>
              <h3>Balises du lieu</h3>
              <p className="hint">
                Coche une balise pour l'inclure dans ce parcours (auto-affecte 10 questions au hasard).
              </p>
              <ul className="balises-list">
                {balises.map((b, i) => {
                  const has = etapeByBalise.get(b.id);
                  return (
                    <li
                      key={b.id}
                      className={has ? "balise-row covered" : "balise-row"}
                    >
                      <input
                        type="checkbox"
                        checked={!!has}
                        onChange={async (e) => {
                          if (e.target.checked) await addBaliseToParcours(b.id);
                          else await clearEtape(b.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        title={has ? "Retirer du parcours" : "Ajouter au parcours"}
                      />
                      <span className="balise-num" onClick={() => setSelectedId(b.id)}>{i + 1}</span>
                      <span className="balise-nom" onClick={() => setSelectedId(b.id)}>{b.nom ?? "Sans nom"}</span>
                      <span className="balise-pos">
                        {has ? `${has.question_ids.length} q.` : "—"}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <>
              <h3>{selectedBalise.nom ?? "Balise sans nom"}</h3>

              {!selectedEtape ? (
                <>
                  <p className="hint">Cette balise n'est pas encore dans le parcours.</p>
                  <button
                    onClick={() => addBaliseToParcours(selectedBalise.id)}
                    className="btn-primary"
                  >
                    + Ajouter au parcours (10 questions au hasard)
                  </button>
                </>
              ) : (
                <>
                  <p className="hint">
                    Une question parmi celles-ci sera tirée au hasard pour chaque groupe au 1ᵉʳ scan.
                  </p>

                  {selectedEtape.question_ids.length > 0 ? (
                    <AssignedQuestionsList
                      questionIds={selectedEtape.question_ids}
                      onChange={(ids) => setQuestionsForBalise(selectedBalise.id, ids)}
                    />
                  ) : (
                    <p className="empty-questions">Aucune question assignée (vide).</p>
                  )}

                  <button onClick={() => setShowPicker(true)} className="btn-primary" style={{ marginTop: 12 }}>
                    + Choisir d'autres questions
                  </button>

                  <button onClick={() => clearEtape(selectedBalise.id)} className="btn-danger" style={{ marginTop: 8 }}>
                    Retirer du parcours
                  </button>
                </>
              )}

              <button onClick={() => setSelectedId(null)} className="btn-secondary" style={{ marginTop: 16 }}>
                ← Toutes les balises
              </button>
            </>
          )}
        </aside>
      </div>

      {showPicker && selectedBalise && (
        <QuestionPicker
          defaultNiveau={parcours.niveau ?? ""}
          alreadySelected={selectedEtape?.question_ids ?? []}
          onConfirm={async (ids) => {
            await setQuestionsForBalise(selectedBalise.id, ids);
            setShowPicker(false);
          }}
          onCancel={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

// ============================================================
//  Map view (read-only positions, cliquable)
// ============================================================

function ParcoursMapView({
  lieu,
  balises,
  etapeByBalise,
  selectedId,
  onSelect,
}: {
  lieu: Lieu;
  balises: Balise[];
  etapeByBalise: Map<string, ParcoursEtape>;
  selectedId: string | null;
  onSelect(id: string | null): void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());

  const W = lieu.image_width ?? 1280;
  const H = lieu.image_height ?? 900;
  const imageUrl = `/${lieu.image_key ?? "stalingrad-map.png"}`;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      crs: L.CRS.Simple,
      minZoom: -3, maxZoom: 2,
      attributionControl: false,
    });
    mapRef.current = map;
    const bounds: L.LatLngBoundsExpression = [[0, 0], [H, W]];
    L.imageOverlay(imageUrl, bounds).addTo(map);
    map.fitBounds(bounds);
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set<string>();
    for (const b of balises) {
      seen.add(b.id);
      const ll: L.LatLngExpression = [H * (1 - b.position_y), W * b.position_x];
      const etape = etapeByBalise.get(b.id);
      const count = etape?.question_ids.length ?? 0;
      let marker = markersRef.current.get(b.id);
      if (!marker) {
        marker = L.marker(ll, {
          draggable: false,
          icon: makeIcon(count, b.id === selectedId),
        });
        marker.addTo(map);
        marker.on("click", () => onSelect(b.id));
        markersRef.current.set(b.id, marker);
      } else {
        marker.setLatLng(ll);
        marker.setIcon(makeIcon(count, b.id === selectedId));
      }
    }
    for (const [id, m] of markersRef.current) {
      if (!seen.has(id)) { m.remove(); markersRef.current.delete(id); }
    }
  }, [balises, etapeByBalise, selectedId, W, H, onSelect]);

  return (
    <div className="map-editor">
      <div ref={containerRef} className="map-canvas" />
    </div>
  );
}

function makeIcon(count: number, selected: boolean): L.DivIcon {
  const label = count > 0 ? String(count) : "·";
  const cls = `balise-pin parcours-pin ${count > 0 ? "covered" : "empty"} ${selected ? "selected" : ""}`;
  return L.divIcon({
    html: `<div class="${cls}">${label}</div>`,
    className: "balise-pin-wrapper",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
}

function hasAnyGps(balises: Balise[]): boolean {
  return balises.some((b) => b.lat != null && b.lng != null);
}

/** Retourne une copie melangee du tableau (Fisher-Yates, in-place sur la copie). */
function shuffleCopy<T>(arr: T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ============================================================
//  Vue satellite (Esri) — centree sur la balise 1
// ============================================================

const ESRI_TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const ESRI_ATTR =
  "Tiles &copy; Esri — Maxar, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS";

function ParcoursSatelliteView({
  balises,
  etapeByBalise,
  selectedId,
  onSelect,
}: {
  balises: Balise[];
  etapeByBalise: Map<string, ParcoursEtape>;
  selectedId: string | null;
  onSelect(id: string | null): void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());

  // Centre = balise 1 (premiere avec coords GPS)
  const center = useMemo(() => {
    const first = balises.find((b) => b.lat != null && b.lng != null);
    return first ? { lat: first.lat!, lng: first.lng! } : { lat: 48.8914, lng: 2.4083 };
  }, [balises]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([center.lat, center.lng], 18);
    mapRef.current = map;
    L.tileLayer(ESRI_TILE_URL, { maxZoom: 20, attribution: ESRI_ATTR }).addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set<string>();
    for (const b of balises) {
      if (b.lat == null || b.lng == null) continue;
      seen.add(b.id);
      const ll: L.LatLngExpression = [b.lat, b.lng];
      const etape = etapeByBalise.get(b.id);
      const count = etape?.question_ids.length ?? 0;
      let marker = markersRef.current.get(b.id);
      if (!marker) {
        marker = L.marker(ll, {
          draggable: false,
          icon: makeIcon(count, b.id === selectedId),
        });
        marker.addTo(map);
        marker.on("click", () => onSelect(b.id));
        markersRef.current.set(b.id, marker);
      } else {
        marker.setLatLng(ll);
        marker.setIcon(makeIcon(count, b.id === selectedId));
      }
    }
    for (const [id, m] of markersRef.current) {
      if (!seen.has(id)) { m.remove(); markersRef.current.delete(id); }
    }
  }, [balises, etapeByBalise, selectedId, onSelect]);

  return (
    <div className="map-editor">
      <div ref={containerRef} className="map-canvas" />
    </div>
  );
}

// ============================================================
//  AssignedQuestionsList — liste les questions assignees
// ============================================================

function AssignedQuestionsList({
  questionIds,
  onChange,
}: {
  questionIds: string[];
  onChange(ids: string[]): void;
}) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Charge les questions une par une (acceptable pour <=50)
        const list = await Promise.all(questionIds.map(async (qid) => {
          try {
            const data = await fetch(
              `${import.meta.env.VITE_API_URL}/api/questions/${qid}`,
              { headers: { Authorization: `Bearer ${localStorage.getItem("orientation-admin-token")}` } }
            ).then((r) => r.json() as Promise<{ ok: boolean; question?: Question }>);
            return data.question ?? null;
          } catch { return null; }
        }));
        setQuestions(list.filter((q): q is Question => q !== null));
      } finally { setLoading(false); }
    })();
  }, [questionIds]);

  function remove(qid: string) {
    onChange(questionIds.filter((id) => id !== qid));
  }

  if (loading) return <p className="hint">Chargement des questions…</p>;

  return (
    <ul className="assigned-questions">
      {questions.map((q) => (
        <li key={q.id} className="assigned-q">
          <span className="aq-emoji">{q.emoji ?? "•"}</span>
          <span className="aq-text">{q.texte}</span>
          {q.niveau && <span className="badge badge-niveau">{q.niveau}</span>}
          <button onClick={() => remove(q.id)} className="aq-remove" title="Retirer">×</button>
        </li>
      ))}
    </ul>
  );
}

// ============================================================
//  QuestionPicker — modal multi-select pour assigner
// ============================================================

function QuestionPicker({
  defaultNiveau,
  alreadySelected,
  onConfirm,
  onCancel,
}: {
  defaultNiveau: string;
  alreadySelected: string[];
  onConfirm(ids: string[]): Promise<void>;
  onCancel(): void;
}) {
  const [niveau, setNiveau] = useState(defaultNiveau);
  const [matiere, setMatiere] = useState("");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Question[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set(alreadySelected));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { questions } = await api.listQuestions({
          niveau: niveau || undefined,
          matiere: matiere || undefined,
          search: search || undefined,
          limit: 100,
        });
        setResults(questions);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [niveau, matiere, search]);

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function save() {
    setSubmitting(true);
    try {
      await onConfirm(Array.from(picked));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-form modal-form-wide" onClick={(e) => e.stopPropagation()}>
        <h2>📚 Choisir les questions</h2>
        <div className="picker-filters">
          <select value={niveau} onChange={(e) => setNiveau(e.target.value)}>
            <option value="">Tous niveaux</option>
            {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <input
            type="text"
            placeholder="Matière"
            value={matiere}
            onChange={(e) => setMatiere(e.target.value)}
          />
          <input
            type="search"
            placeholder="Recherche…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="picker-results">
          {loading ? (
            <p className="hint">Recherche…</p>
          ) : results.length === 0 ? (
            <p className="hint">Aucune question ne correspond.</p>
          ) : (
            <ul className="picker-list">
              {results.map((q) => (
                <li
                  key={q.id}
                  className={picked.has(q.id) ? "picker-item picked" : "picker-item"}
                  onClick={() => toggle(q.id)}
                >
                  <input
                    type="checkbox"
                    checked={picked.has(q.id)}
                    onChange={() => toggle(q.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="aq-emoji">{q.emoji ?? "•"}</span>
                  <div className="picker-content">
                    <div className="picker-text">{q.texte}</div>
                    <div className="picker-meta">
                      {q.niveau && <span className="badge badge-niveau">{q.niveau}</span>}
                      {q.matiere && <span className="badge badge-matiere">{q.matiere}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="modal-actions">
          <span style={{ marginRight: "auto", color: "var(--text-light)", fontSize: 13 }}>
            {picked.size} question{picked.size > 1 ? "s" : ""} sélectionnée{picked.size > 1 ? "s" : ""}
          </span>
          <button onClick={onCancel} className="btn-secondary">Annuler</button>
          <button onClick={save} disabled={submitting} className="btn-primary">
            {submitting ? "Enregistrement…" : "Valider"}
          </button>
        </div>
      </div>
    </div>
  );
}
